// ================================================================================================
// Controller for Light/Dark mode and system preference
// ================================================================================================

const DEBUG_VERBOSE = false;
const SUPPORTS_DARK_MODE = false
const LOG_PREFIX = `[ModeController]`;

export const COLOR_SCHEME_CHANGED = 'color_scheme_changed';

export type ColorSchemeChangedEventDetail = {
	isDarkMode: boolean;
};

export type ColorSchemeChangedEvent = CustomEvent<ColorSchemeChangedEventDetail>;

const COLOR_SCHEME_STORAGE_KEY = 'color-scheme-dark';

class ModeController {
	private darkModeQuery: MediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');

	constructor() {}

	init() {


		if (!SUPPORTS_DARK_MODE) {
			DEBUG_VERBOSE && console.warn(LOG_PREFIX, 'Dark mode is not supported in this theme');
			return;
		}

		// Check for stored preference first, fallback to system preference
		const storedPreference = this.getStoredPreference();

		if (storedPreference !== null) {
			this.setDarkMode(storedPreference, false);
		} else {
			this.setDarkMode(this.darkModeQuery.matches, false);
		}

		// Listen for system preference changes (only if no stored preference)
		this.darkModeQuery.addEventListener('change', (event) => {
			if (!this.isPreferenceStored()) {
				this.setDarkMode(event.matches, false);
			}
		});
	}

	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	private getStoredPreference(): boolean | null {
		try {
			const raw = window.localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
			if (raw === null) return null;
			return JSON.parse(raw) as boolean;
		} catch (error) {
			DEBUG_VERBOSE && console.warn(LOG_PREFIX, 'Unable to read stored color scheme', error);
			return null;
		}
	}

	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	private isPreferenceStored(): boolean {
		return this.getStoredPreference() !== null;
	}

	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	private persistPreference(isDarkMode: boolean) {
		try {
			window.localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, JSON.stringify(isDarkMode));
		} catch (error) {
			DEBUG_VERBOSE && console.warn(LOG_PREFIX, 'Unable to persist color scheme', error);
		}
	}

	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	setDarkMode(isDarkMode: boolean, store = true) {
		document.body.classList.toggle('hhds-dark', isDarkMode);
		document.body.classList.toggle('hhds-light', !isDarkMode);
		if (store) {
			this.persistPreference(isDarkMode);
		}
		this.dispatchColorSchemeChanged(isDarkMode);
	}

	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	private dispatchColorSchemeChanged(isDarkMode: boolean) {
		const colorSchemeChangedEvent = new CustomEvent<ColorSchemeChangedEventDetail>(COLOR_SCHEME_CHANGED, {
			detail: {
				isDarkMode,
			},
		});

		document.documentElement.dispatchEvent(colorSchemeChangedEvent);
	}
}

export const modeController = new ModeController();
